/**
 * TicketedEventAdmission Entity Schemas
 *
 * An Admission is one admittable seat on a TicketedEventOrder — the thing a
 * door scan acts on. An order item that used to carry `quantity: 4` becomes
 * four admissions, so a multi-seat order can be partially checked in (R21).
 *
 * This is the CLIENT-VISIBLE shape only: what a reader of the order payload or
 * the my-tickets row needs to render a per-seat row. Internal columns stay off
 * it. Per KTD8 `id` is the public id — never a BigInt, which would throw at
 * `JSON.stringify` rather than at typecheck.
 *
 * The backing model landed in U1 as `TicketedEventAdmission`
 * (`apps/backend/prisma/schema/ticketed-events.prisma`) and carries more than
 * this shape does. Everything below is a column on that row, but the row also
 * has `answers`, `idempotencyKey`, `statusReason`, `priceCents`,
 * `contributionCents`, `overCapacity`, `createdByUsersOnSchoolsId` and the three
 * removal timestamps, and NONE of them are declared here. That is deliberate,
 * and the bar for adding one is not "a producer selects it": every field on this
 * object flows into `TicketedEventAdmissionPublicSchema` below unless it is
 * omitted there, and that shape is read from unauthenticated bearer links.
 * `answers` in particular is per-attendee custom-question data — the most
 * sensitive thing on the row — and must never reach a forwardable link.
 *
 * Naming (KTD1): the type and its fields say `admission`; buyer- and
 * admin-facing copy says "ticket" or "registration", never "admission".
 */

import { z } from 'zod'

/**
 * `active` is the only admittable state. `deactivated` is a seat released by an
 * admin, `cancelled` follows its order, `voided` is an issuing mistake undone.
 * `active` and `deactivated` are both "live" for the check-in rollup (KTD6) and
 * for capacity: a deactivated seat was released from its holder, not returned
 * to inventory. `cancelled` and `voided` seats are not live. The backend's one
 * list is `LIVE_ADMISSION_STATUSES` in
 * `apps/backend/src/services/ticketed-events/inventory/statements.ts`, exported
 * from the event inventory's entry point (`inventory/index.ts`).
 */
export const TicketedEventAdmissionStatusSchema = z.enum(['active', 'deactivated', 'cancelled', 'voided'])
export type TicketedEventAdmissionStatus = z.infer<typeof TicketedEventAdmissionStatusSchema>

/** How the seat came to exist — a purchase, a comp, or one issued at the door. */
export const TicketedEventAdmissionOriginSchema = z.enum(['purchase', 'comp', 'box_office', 'walk_up', 'import'])
export type TicketedEventAdmissionOrigin = z.infer<typeof TicketedEventAdmissionOriginSchema>

export const TicketedEventAdmissionSchema = z.object({
  id: z.string(),
  status: TicketedEventAdmissionStatusSchema,
  origin: TicketedEventAdmissionOriginSchema,
  // The public id of the order item this seat was expanded from, so a seat that
  // arrives in a FLATTENED array — the admin order and the my-tickets row both
  // carry every seat in one list — can still be attributed to its line. Without
  // it, two seats from different tiers are both `seatIndex: 0` with nothing to
  // tell them apart, and `ticketTypeId` is nullable so it is not a fallback key.
  //
  // Nullable because not every seat has an order item behind it: `comp`,
  // `box_office` and `walk_up` origins are issued directly, which is the same
  // nullable order/order-item the underlying row models.
  orderItemId: z.string().nullable(),
  // The public id of the tier this seat was issued against.
  //
  // Nullable ON PURPOSE, and not because today's data needs it to be: every
  // order item carries a required `ticket_type_id`, so every seat expanded from
  // one inherits a tier. It is nullable because this declaration ships BEFORE
  // the table that backs it, and the two directions of later change are not
  // symmetric — tightening a declared-nullable field to always-populated costs
  // an installed build nothing, while loosening a declared-required one breaks
  // its parse outright, which is the exact regression R46/AE15 exist to
  // prevent.
  //
  // U1 landed `ticket_type_id` NOT NULL — every event has at least one ticket
  // type, so the door, a comp and an import all pick one — and this stays
  // NULLABLE anyway. Tightening it would buy a client nothing it does not
  // already have (a payload that never sends null parses identically either
  // way), while it would foreclose the tierless seat cheaply: the column can be
  // relaxed with one additive alter, but a shipped build that rejects null on
  // this field cannot be. Tighten it only alongside a producer that needs the
  // narrower type.
  ticketTypeId: z.string().nullable(),
  // The tier's name, SNAPSHOTTED at issue time exactly as
  // `TicketedEventOrderItemSchema.ticketTypeName` is — renaming a tier later
  // must not rewrite what an already-issued ticket says. It lives here because
  // no payload that carries seats can resolve a tier id to a name: the public
  // order's `items[]` are anonymous (`{ticketTypeName, quantity}`, no id) and
  // the my-tickets row has no `items[]` at all, so "Ticket 2 of 4, General
  // Admission" is only expressible if the seat names its own tier.
  //
  // Nullable on the same terms as `ticketTypeId` above, and for the same
  // reason: NOT because today's data needs it — `ticket_type_name` is a
  // required column on every order item, so every seat expanded from one
  // inherits a name — but because this declaration ships BEFORE the table that
  // backs it, and loosening a declared-required field later is the exact
  // regression R46/AE15 exist to prevent. U1 landed `ticket_type_name` NOT NULL
  // for the same reason `ticket_type_id` is, and this field stays nullable on
  // the same terms — see the note on `ticketTypeId` directly above.
  ticketTypeName: z.string().nullable(),
  // Position of this seat within its order item, so a client can label
  // "Ticket 2 of 4" without inventing an order. Nullable for seats that were
  // never numbered.
  seatIndex: z.number().int().nullable(),
  // Who occupies the seat, when the event collects per-attendee data. Null on
  // an order that only ever named its buyer.
  holderFirstName: z.string().nullable(),
  holderLastName: z.string().nullable(),
  holderEmail: z.string().nullable(),
  // ISO timestamp of this seat's own check-in; null until it is scanned. This
  // is the per-seat truth. Exactly two coarser stamps roll up over it (KTD6):
  // the order item's, `TicketedEventOrderItemSchema.checkedInAt` in
  // `packages/shared-types/src/schemas/entities/ticketed-event-order.ts`, and
  // the door's order row, `PublicCheckInAttendeeSchema.checkedInAt` in
  // `packages/shared-types/src/api/ticketed-events/check-in-links.ts`. No order
  // DTO declares a `checkedInAt` of its own, so "the order-level stamp" means
  // the door row and nothing else.
  //
  // Both take the SAME rule: non-null only when every live admission
  // underneath is checked in, and then the LATEST of those stamps, so each
  // field keeps its all-seats meaning (KTD6). For the door row that was a
  // change from the earliest stamp it reported before, and R46 did not prevent
  // it: R46 is scoped to fields a shipped mobile build reads from the order
  // payload, while the door row is consumed by a web surface that redeploys on
  // every merge.
  //
  // The P3 backend half (U4, SCH-1622) landed both derivations; the shared rule
  // is `apps/backend/src/services/ticketed-events/shared/admission-derivation.ts`.
  // The item column still exists and the admin toggles and the door still
  // write it, mirrored onto the seats in the same transaction (SCH-1758); see
  // the note on `TicketedEventOrderItemSchema.checkedInAt`. U8 adds the
  // per-admission door rows on top; it does not establish this meaning.
  checkedInAt: z.string().nullable(),
  createdAt: z.string()
})
export type TicketedEventAdmission = z.infer<typeof TicketedEventAdmissionSchema>

/**
 * The same seat, minus `holderEmail`, for payloads that are read WITHOUT
 * authentication.
 *
 * The my-tickets link and the registrant-facing order link are bearer
 * credentials: no auth middleware runs on them, the my-tickets link does not
 * expire, and whoever holds one reads the payload. `holderEmail` is the
 * harvestable field on a seat, so a forwarded link must not carry the email
 * addresses of the other people on the order. The holder NAMES stay — the buyer
 * usually typed them in themselves and needs them to tell one ticket from
 * another.
 *
 * Both routes now enforce it at RUNTIME, at different grains.
 *
 * On my-tickets this declaration IS the enforcement point, not a hint:
 * `listMyTickets` is typed as `TicketedEventOrderSummary[]`
 * (`apps/backend/src/services/ticketed-events/list-my-tickets.ts`) and
 * `publicMyTicketsUseCase` parses the mapped array through
 * `TicketedEventOrderSummarySchema` before responding
 * (`apps/backend/src/api/ticketed-events/v1/use-cases/(public)/my-tickets/use-case.ts`).
 * Zod strips undeclared keys, so the shape reached from here is a live OUTBOUND
 * FIELD WHITELIST there. Declaring holder PII on it widens that whitelist, and
 * the leak arrives the moment a producer selects the column.
 *
 * On the public order only the seats are parsed. The producer is a hand-written
 * `PublicOrderDto` interface
 * (`apps/backend/src/services/ticketed-events/get-public-order.ts`) returned
 * unparsed by `publicGetOrderByReferenceHandler`
 * (`apps/backend/src/api/ticketed-events/v1/parity/handlers.ts`), but its
 * `admissions` array (U17, SCH-1635) is read with a select that never takes
 * `holderEmail` (`PUBLIC_FLAT_ADMISSION_ARGS` in `shared/mappers.ts`) and is
 * parsed through this schema on the way out, so this declaration is the
 * whitelist for the seats there too. Client-side parsing is not a mitigation:
 * a leaked email is already on the wire and readable by anyone holding the
 * link. Retyping `PublicOrderDto` as `TicketedEventOrderPublic` is still worth
 * doing but is not sufficient on its own: TypeScript rejects excess properties
 * only on object literals.
 *
 * `packages/shared-types/src/api/ticketed-events/check-in-links.ts` draws the
 * same line for the door surface. Do not "simplify" this back to the full
 * shape.
 *
 * Derived with `.omit` rather than retyped, so the two cannot drift apart.
 */
export const TicketedEventAdmissionPublicSchema = TicketedEventAdmissionSchema.omit({ holderEmail: true })
export type TicketedEventAdmissionPublic = z.infer<typeof TicketedEventAdmissionPublicSchema>
